import { useCallback } from 'react'
import { PREFERENCE_CONTRACT_VERSION, type PreferenceDefinition } from '@solum/experience'
import { useUserSettingPreference } from '../data/useUserSettingPreference'

/**
 * A person's nested view choice, shared across parent records (#2375/F-84).
 * The durable address is host + parent entity + relationship slot, never the
 * record UUID. Values stay in the existing caller-owned `core.user_setting`;
 * governed view definitions remain separate `core.view` rows.
 */
export const NESTED_VIEWS_KEY = 'relationships.views'

export type NestedViewHost = 'console'

/** The durable key. Deliberately takes no record id — see above. */
export function nestedViewKey(host: NestedViewHost, parentEntity: string, slotId: string): string {
  return `${host}|${parentEntity}|${slotId}`
}

const choices = (value: unknown): Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}

/** Preserve the existing flat `console|parentEntity|slot` wire document. */
const preference: PreferenceDefinition<string, string, string> = {
  contractVersion: PREFERENCE_CONTRACT_VERSION,
  settingKey: NESTED_VIEWS_KEY,
  read: (settings, address) => {
    const value = choices(settings?.[NESTED_VIEWS_KEY])[address]
    return typeof value === 'string' && value.length > 0 ? value : ''
  },
  reduce: (_current, update) => update,
  write: (settings, address, updates) => {
    const next = { ...choices(settings[NESTED_VIEWS_KEY]) }
    const chosen = updates.at(-1)
    if (chosen) next[address] = chosen
    return next
  },
}

export function useNestedViewChoice(
  parentEntity: string, slotId: string | null | undefined, host: NestedViewHost = 'console',
): [string, (viewId: string) => void] {
  const key = slotId ? nestedViewKey(host, parentEntity, slotId) : null
  const state = useUserSettingPreference(preference, key ?? '', key ?? '', 0)
  const choose = useCallback((viewId: string) => {
    if (key && viewId) state.update(viewId)
  }, [key, state.update])
  return [key ? state.value : '', choose]
}
